import { useEffect } from "react";
import { Button } from "../ui/button";
import { useModal } from "@/hooks/useModal";
import { unsubscribeToNotification } from "@/helpers/unsubscribeToNotification";
import { useFirebaseFunction } from "@/hooks/useFirebaseFunction";
import { useSingleGamePack } from "@/hooks/useSingleGamePack";
import { useStartGame } from "@/hooks/useStartGame";
import type { FirebaseTimestamp, GameActionStartRet, UserGamePack } from "@skillscratch/shared-types";
import type { StartGameProps } from "./PurchaseModal";
import { useState } from "react";
import { useViewTransition } from "@/hooks/useViewTransition";
import { ErrorModal } from "./ErrorModal";
import posthog from "posthog-js";
import { callFirebaseFunction } from "@/bridge/callFirebaseFunction";
import { Timestamp } from "firebase/firestore";
import { subscribeToNotification } from "@/helpers/subscribeToNotification";

type UpdateUserPrivateInfoRequest = {
    updates: Partial<{
      lastFreeCardPromo?: FirebaseTimestamp
    }>;
};
  
type UpdateUserPrivateInfoResponse = {
    success: boolean;
};


export function FreeCardModal() {
    const [startGameProps, setStartGameProps] = useState<StartGameProps | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const { navigateNormal } = useViewTransition(); 
    

    const { closeModalWithoutAnimation, openModal } = useModal();
    const [, isFreeCardLoading] = useFirebaseFunction<void, boolean>(
        "addFreeCard",
        (fn) => fn(),
        [],
    )

    const [userGamePack, isGamePackLoading, ,] = useSingleGamePack("crazy-99s");

    const { startGameHandler } = useStartGame({
        onSuccess: (startGameRet: GameActionStartRet, unplayedCard: UserGamePack) => {
            setStartGameProps({
                startGameRet,
                unplayedCard,
            });
            setIsLoading(false);
        },
        onError: (error: unknown) => {
            openModal(<ErrorModal message={String(error)} />);
            setIsLoading(false);
            console.error(error);
            posthog.capture("free_card_loading_error", {
                error,
            })
        }
    });

    useEffect(() => {
        const today = new Date();
        const todayString = today.toISOString().split('T')[0];
        unsubscribeToNotification(`free_card_${todayString}`);

        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowString = tomorrow.toISOString().split('T')[0];
        subscribeToNotification(`free_card_${tomorrowString}`);
        posthog.capture('add_free_card_modal_shown');
    }, [])

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        setIsLoading(true);

        if (isFreeCardLoading || isGamePackLoading || !userGamePack) {
            return;
        }

        const initGame = async () => {
            await startGameHandler(userGamePack as UserGamePack);
        };
        
        initGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFreeCardLoading, isGamePackLoading, userGamePack])

        useEffect(() => {
            const updateLastAskedTimestamp = async () => {
                try {
                    const response = await callFirebaseFunction<
                        UpdateUserPrivateInfoRequest,
                        UpdateUserPrivateInfoResponse
                    >(
                        "updateUserPrivateInfo",
                        { 
                            updates: { 
                                lastFreeCardPromo: Timestamp.fromDate(new Date())
                            } 
                        }
                    );
                    
                    console.log("Updated lastAskedForPushNotifications:", response.success);
                } catch (error) {
                    console.error("Failed to update lastAskedForPushNotifications:", error);
                }
            };
            
            updateLastAskedTimestamp();
        }, []);

    const onClickHandler = async () => {
        if (isLoading) {
            return;
        }
        
        if (!startGameProps) {
            return;
        }
        
        const { startGameRet, unplayedCard } = startGameProps;

        navigateNormal("/game", {
            replace: false,
            viewTransition: true,
            state: {
                gamePackId: unplayedCard.id,
                gameThemeId: unplayedCard.gameThemeId,
                cardCount: unplayedCard.count,
                startGameRet,
            },
        })
        closeModalWithoutAnimation();
    }

    return (
        <div className="bg-white rounded-xl shadow-lg py-8 px-2 w-80 h-80 text-center flex flex-col justify-between">
            <div>
                <div className="mx-auto mb-3">
                    <span className='text-blue-dark text-4xl font-bold'>Free $10 Card</span>
                </div>

                <h2 className="text-blue-dark text-2xl font-medium mb-4 mx-8 text-blue-dark">Here's a $10 card on us. You're so close to a big win 🔥</h2>
                {/* <p className="text-blue-dark mb-8 mx-5 text-xl overflow-auto max-h-32 break-words">Error</p> */}
            </div>
            
            <div className="px-7 pt-5 h-full flex flex-col space-between">
                <div className='h-4'/>
                <div>
                    <Button 
                        className="py-7 bg-blue text-white text-2xl font-bold w-full rounded-lg"
                        shine
                        onClick={() => onClickHandler()}
                    >
                        {isFreeCardLoading || isGamePackLoading || isLoading ? 'Loading...' : 'Claim $10 card!'}
                    </Button>
                </div>
                <div className='h-4'/>
            </div>
        </div>
    );
};